//import store from "../redux/store";
import { applyActionCode, checkActionCode, confirmPasswordReset, createUserWithEmailAndPassword, indexedDBLocalPersistence, isSignInWithEmailLink, multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, setPersistence, signInWithCredential, signInWithCustomToken as firebaseSignInWithCustomToken, signInWithEmailAndPassword, signInWithEmailLink, signInWithPhoneNumber, updatePassword, verifyPasswordResetCode, updateProfile, updatePhoneNumber, TotpMultiFactorGenerator } from "firebase/auth";
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectEmpty, isObjectNotEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { toLower } from "lodash";
import { auth } from ".";
import User from "../models/User";
import { toE164PhoneNumber } from "../utils/stringFormatter";
import Address from "../models/Address";
import { sendEmailVerificationLink, sendPasswordResetLink, sendSignInWithEmailLink } from "./Email";
import { getFirebaseErrorCode } from "../utils/errorUtils";
import AuthUser from "../models/AuthUser";
import { unsubscribeFromPush } from "../services/pushSubscriptionService";
import moment from "moment-timezone";
//import { login } from "../redux/ducks/auth";

const asyncSignInWithEmailAndPassword = (authInstance, userName, password) => {
    return new Promise((resolve, reject) => {
        return signInWithEmailAndPassword(authInstance, userName, password)
            .then((userCredential) => resolve(userCredential))
            .catch((err) => reject(err));
    });
};

const asyncCreateUserWithEmailAndPassword = (authInstance, email, password) => {
    return new Promise((resolve, reject) => {
        return createUserWithEmailAndPassword(authInstance, toLower(email), password)
            .then((userCredential) => resolve(userCredential))
            .catch((err) => reject(err));
    });
};

const asyncUpdateProfile = (authUser, displayName = null, photoUrl = null) => {
    return new Promise((resolve, reject) => {
        return updateProfile(authUser.user, { displayName: displayName, photoURL: photoUrl })
            .then(() => resolve())
            .catch((err) => reject(err));
    });
};

const asyncSetPersistence = (authInstance, persistence) => {
    return new Promise((resolve, reject) => {
        return setPersistence(authInstance, persistence)
            .then(() => resolve())
            .catch((err) => reject(err));
    });
};

const asyncSignInWithEmailLink = (authInstance, email, emailLink) => {
    return new Promise((resolve, reject) => {
        return signInWithEmailLink(authInstance, toLower(email), emailLink)
            .then((userCredential) => resolve(userCredential))
            .catch((err) => reject(err));
    });
};

const asyncSignInWithPhoneNumber = (authInstance, phone, appVerifier) => {
    return new Promise((resolve, reject) => {
        return signInWithPhoneNumber(authInstance, phone, appVerifier)
            .then((confirmationResult) => resolve(confirmationResult))
            .catch((err) => reject(err));
    });
};

const asyncSignInWithCredential = (authInstance, authCredential) => {
    return new Promise((resolve, reject) => {
        return signInWithCredential(authInstance, authCredential)
            .then((userCredential) => resolve(userCredential))
            .catch((err) => reject(err));
    });
};

const asyncSignInWithCustomToken = (authInstance, customToken) => {
    return new Promise((resolve, reject) => {
        return firebaseSignInWithCustomToken(authInstance, customToken)
            .then((userCredential) => resolve(userCredential))
            .catch((err) => reject(err));
    });
};

const asyncVerifyPhoneNumber = (phoneAuthProvider, phoneInfoOptions, appVerifier) => {
    return new Promise((resolve, reject) => {
        return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, appVerifier)
            .then((verificationId) => resolve(verificationId))
            .catch((err) => reject(err));
    });
};

// *** Auth API ***
// no quotes, backslash or backtick: a generated password is never typed, but it does travel through
// callable payloads and logs, where those characters invite escaping bugs
const GENERATED_PASSWORD_CHARACTER_SETS = ['abcdefghijklmnopqrstuvwxyz', 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '0123456789', '!@#$%^&*()-_=+[]{};:,.?'];

// a plain modulo favors the low indices whenever the range is not a multiple of the set size, so
// draws above the largest whole multiple are thrown away and redrawn
const toSecureRandomIndex = (size) => {
    const draw = new Uint32Array(1);
    const limit = Math.floor(0x100000000 / size) * size;
    do {
        globalThis.crypto.getRandomValues(draw);
    } while (draw[0] >= limit);

    return draw[0] % size;
};

class AuthService {
    constructor() { }

    // Register
    static doCreateUserWithEmailAndPassword = async (email, password) => {
        return await asyncCreateUserWithEmailAndPassword(auth, toLower(email), password);
    }

    static doCreateUserWithEmailAndName = async (email, firstName, lastName) => {
        // console.log(moment().toISOString());
        const password = AuthService.randomPasswordGenerator(16);
        let authUser = null;
        try {
            authUser = await AuthService.doCreateUserWithEmailAndPassword(email, password);
        } catch (error) {
            const errorCode = getFirebaseErrorCode(error);
            console.log(errorCode);
            // if (isStringNotEmpty(errorCode) && errorCode === "auth/email-already-in-use") {
            //     try {
            //         const existingAuthUser = await AuthUser.getByEmail(toLower(email));
            //         if (isObjectNotEmpty(existingAuthUser) && isStringNotEmpty(existingAuthUser.uid)) {
            //             return existingAuthUser.uid;
            //         }
            //     }
            //     catch (err) {
            //         throw err;
            //     }
            // }

            throw error;
        }

        // console.log('authUser', authUser);
        // console.log(moment().toISOString());

        if (isObjectNotEmpty(authUser) && isObjectNotEmpty(authUser.user) && isStringNotEmpty(authUser.user.uid)) {
            // update auth user with display name

            // sign in new auth user

            try {
                const currentUser = { ...authUser.user };
                localStorage.setItem('authUser', JSON.stringify(currentUser));
            } catch (err) {
                console.log(err);
            }

            // console.log('user created and signed in');
            // console.log(moment().toISOString());

            if (isStringNotEmpty(firstName) && isStringNotEmpty(lastName)) {
                let displayName = `${firstName} ${lastName}`;

                try {
                    await asyncUpdateProfile(authUser, displayName, null);
                    // console.log('updated displayName');
                    // console.log(moment().toISOString());
                } catch (err) {
                    console.log(err);
                }
            }

            return authUser.user.uid;
        }

        return null;
    }

    // consentSource names the screen the account is created on, which dates its marketing consent to the creation
    static doCreateUserWithEmailAndPasswordAndName = async (email, password, firstName, lastName, profile = {}, consentSource = null) => {
        // console.log(moment().toISOString());
        let authUser = null;
        try {
            authUser = await AuthService.doCreateUserWithEmailAndPassword(email, password);
        } catch (error) {
            const errorCode = getFirebaseErrorCode(error);
            console.log(errorCode);
            // if (isStringNotEmpty(errorCode) && errorCode === "auth/email-already-in-use") {
            //     try {
            //         const existingAuthUser = await AuthUser.getByEmail(toLower(email));
            //         if (isObjectNotEmpty(existingAuthUser) && isStringNotEmpty(existingAuthUser.uid)) {
            //             return existingAuthUser.uid;
            //         }
            //     }
            //     catch (err) {
            //         throw err;
            //     }
            // }

            throw error;
        }

        // console.log('authUser', authUser);
        // console.log(moment().toISOString());

        if (isObjectNotEmpty(authUser) && isObjectNotEmpty(authUser.user) && isStringNotEmpty(authUser.user.uid)) {
            const user = new User({
                userId: authUser?.user?.uid,
                ...profile
            });
            if (isStringNotEmpty(consentSource)) {
                user.dateMarketingConsent(consentSource);
            }
            const newUser = await user.create();
            // console.log('newUser', newUser);
            // console.log(moment().toISOString());

            if (isObjectNotEmpty(newUser)) {
                // update auth user with display name

                // sign in new auth user

                try {
                    const currentUser = { ...authUser.user };
                    localStorage.setItem('authUser', JSON.stringify(currentUser));
                } catch (err) {
                    console.log(err);
                }

                // console.log('user created and signed in');
                // console.log(moment().toISOString());

                if (isStringNotEmpty(firstName) && isStringNotEmpty(lastName)) {
                    let displayName = `${firstName} ${lastName}`;

                    try {
                        await asyncUpdateProfile(authUser, displayName, null);
                        // console.log('updated displayName');
                        // console.log(moment().toISOString());
                    } catch (err) {
                        console.log(err);
                    }
                }

                return newUser;
            }
        }

        return null;
    }

    static doCreateUserWithEmailAndPasswordAndProfile = async (email, password, phone, firstName, middleName, lastName, suffix, gender, dateOfBirth, streetAddress1, streetAddress2, city, state, postalCode, country) => {
        // console.log(moment().toISOString());
        const authUser = await AuthService.doCreateUserWithEmailAndPassword(email, password);
        // console.log('authUser', authUser);
        // console.log(moment().toISOString());
        let newUser = null;
        if (isObjectNotEmpty(authUser) && isObjectNotEmpty(authUser.user) && isStringNotEmpty(authUser.user.uid)) {
            // update auth user with display name

            // sign in new auth user
            try {
                const currentUser = { ...authUser.user };
                localStorage.setItem('authUser', JSON.stringify(currentUser));
            } catch (err) {
                console.log(err);
            }

            // console.log('user created and signed in');
            // console.log(moment().toISOString());

            if (isStringNotEmpty(firstName) && isStringNotEmpty(lastName)) {
                let displayName = `${firstName} ${lastName}`;

                try {
                    await asyncUpdateProfile(authUser, displayName, null);
                    // console.log('updated displayName');
                    // console.log(moment().toISOString());
                } catch (err) {
                    console.log(err);
                }
            }

            // Create a user in your Firebase database
            newUser = await new User({
                userId: authUser.user.uid,

                email: toLower(email),
                phone: toE164PhoneNumber(phone),

                firstName: firstName,
                middleName: middleName,
                lastName: lastName,
                suffix: suffix,

                gender: gender,
                dateOfBirth: dateOfBirth,

                shippingAddress: new Address({
                    firstName: firstName,
                    lastName: lastName,
                    streetAddress1: streetAddress1,
                    streetAddress2: streetAddress2,
                    city: city,
                    state: state,
                    postalCode: postalCode,
                    country: country,
                    phone: phone
                }).toDto(),
                billingAddress: new Address({
                    firstName: firstName,
                    lastName: lastName,
                    streetAddress1: streetAddress1,
                    streetAddress2: streetAddress2,
                    city: city,
                    state: state,
                    postalCode: postalCode,
                    country: country,
                    phone: phone
                }).toDto(),

                isRep: false,
                isAdmin: false,
                isSuperAdmin: false,
                isFriendsAndFamily: false
            }).create();
            // console.log('newUser', newUser);
            // console.log(moment().toISOString());
        }

        return { authUser: authUser, user: newUser };
    }

    static doSendSignInLinkToEmail = async (email, signInUrl) => {
        try {
            // first check to see if account exists with this email address 
            // (if no account exists, this will end up creating a new account which we don't want)
            const exists = await AuthUser.existsByEmail(toLower(email));
            if (isBooleanTrue(exists)) {
                const success = await sendSignInWithEmailLink(toLower(email), signInUrl);

                // The link was successfully sent. Inform the user.
                // Save the email locally so you don't need to ask the user for it again
                // if they open the link on the same device.
                localStorage.setItem('emailForSignIn', toLower(email));

                return success;
            }
        } catch (error) {
            console.log(error);
        }

        return false;
    }

    static doIsSignInWithEmailLink = async (emailLink) => {
        return isSignInWithEmailLink(auth, emailLink);
    }

    static doSignInWithEmailLink = async (email, emailLink) => {
        try {
            await asyncSetPersistence(auth, indexedDBLocalPersistence);
            const userCredentials = await asyncSignInWithEmailLink(auth, email, emailLink);

            if (isObjectNotEmpty(userCredentials) && isObjectNotEmpty(userCredentials.user)) {
                if (isBooleanTrue(userCredentials?.user?.disabled)) {
                    await auth.signOut();
                    localStorage.removeItem('authUser');

                    return null;
                }

                try {
                    const currentUser = { ...userCredentials.user };
                    localStorage.setItem('authUser', JSON.stringify(currentUser));
                } catch (err) {
                    console.log(err);
                }
                //store.dispatch(login(currentUser, true));

                return userCredentials;
            }
        } catch (error) {
            throw error;
        }

        return null;
    }

    static doSendSignInCodeToPhoneNumber = async (phone, appVerifier) => {
        if (isStringNotEmpty(phone)) {
            try {
                // first check to see if account exists with this phone number
                // (if no account exists, this will end up creating a new account which we don't want)
                const exists = await AuthUser.existsByPhoneNumber(toE164PhoneNumber(phone));
                if (isBooleanTrue(exists)) {
                    const confirmationResult = await asyncSignInWithPhoneNumber(auth, phone, appVerifier);

                    return confirmationResult.verificationId;
                }
            } catch (error) {
                // Error; SMS not sent
                throw error;
            }
        }

        return null;
    }

    static doSignInWithPhoneNumber = async (verificationId, code) => {
        try {
            if (isStringNotEmpty(verificationId) && isStringNotEmpty(code)) {
                await asyncSetPersistence(auth, indexedDBLocalPersistence);
                const authCredential = PhoneAuthProvider.credential(verificationId, code);
                const userCredentials = await asyncSignInWithCredential(auth, authCredential);

                if (isObjectNotEmpty(userCredentials) && isObjectNotEmpty(userCredentials.user)) {
                    if (isBooleanTrue(userCredentials?.user?.disabled)) {
                        await auth.signOut();
                        localStorage.removeItem('authUser');

                        return false;
                    }

                    try {
                        const currentUser = { ...userCredentials.user };
                        localStorage.setItem('authUser', JSON.stringify(currentUser));
                    } catch (err) {
                        console.log(err);
                    }
                    //store.dispatch(login(currentUser, true));

                    return true;
                }
            }
        } catch (error) {
            throw error;
        }

        return false;
    }

    static doGetMFAEnrollments = () => {
        if (isObjectNotEmpty(auth.currentUser)) {
            const multiFactorUser = multiFactor(auth.currentUser);

            return multiFactorUser.enrolledFactors;
        }

        return [];
    }

    static doUnenrollMFA = async (mfaInfo) => {
        if (isObjectNotEmpty(auth.currentUser)) {
            const multiFactorUser = multiFactor(auth.currentUser);
            try {
                await multiFactorUser.unenroll(mfaInfo);

                return true;
            } catch (err) {
                throw err;
            }
        }

        return false;
    }

    static doSendCodeToPhoneNumberFor2FA = async (phone, appVerifier) => {
        if (isStringNotEmpty(phone)) {
            try {
                const multiFactorUser = multiFactor(auth.currentUser);
                const multiFactorSession = await multiFactorUser.getSession();

                // Send verification code
                const phoneAuthProvider = new PhoneAuthProvider(auth);
                const phoneInfoOptions = {
                    phoneNumber: phone,
                    session: multiFactorSession
                };
                const verificationId = await asyncVerifyPhoneNumber(phoneAuthProvider, phoneInfoOptions, appVerifier);
                console.log(verificationId);

                return verificationId;
            } catch (error) {
                console.log(error);
                // Error; SMS not sent
                throw error;
            }
        }

        return null;
    }

    static doAddPhoneNumberFor2FA = async (verificationId, code, nickname) => {
        try {
            if (isStringNotEmpty(verificationId) && isStringNotEmpty(code)) {
                // Obtain verification code from user.
                const multiFactorUser = multiFactor(auth.currentUser);
                const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, code);
                const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
                await multiFactorUser.enroll(multiFactorAssertion, nickname);
                // Second factor enrolled.
                //  const userCredentials = await reauthenticateWithCredential(auth.currentUser, phoneAuthCredential);

                // if (isObjectNotEmpty(userCredentials) && isObjectNotEmpty(userCredentials.user)) {
                //     const currentUser = { ...userCredentials.user };
                //     localStorage.setItem('authUser', JSON.stringify(currentUser));

                //     console.log('User Credentials');
                //     console.log(userCredentials);
                //     // console.log(userCredentials.user);
                //     // console.log(currentUser);
                //     // console.log(JSON.stringify(userCredentials.user))

                //     return true;
                // }
                //return await doSignOut();

                return true;
            }
        } catch (error) {
            throw error;
        }

        return false;
    }

    static doSendCodeToPhoneNumberFor2FASignIn = async (hint, resolver, appVerifier) => {
        try {
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const phoneInfoOptions = {
                multiFactorHint: hint,
                session: resolver.session
            };
            // console.log(phoneInfoOptions);
            const verificationId = await asyncVerifyPhoneNumber(phoneAuthProvider, phoneInfoOptions, appVerifier);
            // console.log(verificationId);
            return verificationId;
        } catch (error) {
            throw error;
        }
    }

    static doSignInWithPhoneNumberFor2FA = async (verificationId, code, resolver) => {
        try {
            if (isStringNotEmpty(verificationId) && isStringNotEmpty(code) && isNotNullOrUndefined(resolver)) {
                // Obtain verification code from user.
                await asyncSetPersistence(auth, indexedDBLocalPersistence);
                const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, code);
                const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
                const userCredentials = await resolver.resolveSignIn(multiFactorAssertion);

                if (isObjectNotEmpty(userCredentials) && isObjectNotEmpty(userCredentials.user)) {
                    if (isBooleanTrue(userCredentials?.user?.disabled)) {
                        await auth.signOut();
                        localStorage.removeItem('authUser');

                        return false;
                    }

                    try {
                        const currentUser = { ...userCredentials.user };
                        localStorage.setItem('authUser', JSON.stringify(currentUser));
                    } catch (err) {
                        console.log(err);
                    }
                    //store.dispatch(login(currentUser, true));

                    // console.log('User Credentials');
                    // console.log(userCredentials);
                    // console.log(userCredentials.user);
                    // console.log(currentUser);
                    // console.log(JSON.stringify(userCredentials.user))

                    return true;
                }
            }
        } catch (error) {
            throw error;
        }

        return false;
    }

    static doSignInWithTOTPFor2FA = async (uid, code, resolver) => {
        try {
            if (isStringNotEmpty(code) && isNotNullOrUndefined(resolver)) {
                // Obtain verification code from user.
                await asyncSetPersistence(auth, indexedDBLocalPersistence);

                const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(uid, code);
                const userCredentials = await resolver.resolveSignIn(multiFactorAssertion);

                if (isObjectNotEmpty(userCredentials) && isObjectNotEmpty(userCredentials.user)) {
                    if (isBooleanTrue(userCredentials?.user?.disabled)) {
                        await auth.signOut();
                        localStorage.removeItem('authUser');

                        return false;
                    }

                    try {
                        const currentUser = { ...userCredentials.user };
                        localStorage.setItem('authUser', JSON.stringify(currentUser));
                    } catch (err) {
                        console.log(err);
                    }
                    //store.dispatch(login(currentUser, true));

                    // console.log('User Credentials');
                    // console.log(userCredentials);
                    // console.log(userCredentials.user);
                    // console.log(currentUser);
                    // console.log(JSON.stringify(userCredentials.user))

                    return true;
                }
            }
        } catch (error) {
            throw error;
        }

        return false;
    }

    // Sign In
    static doSignInWithEmailAndPassword = async (email, password) => {
        try {
            await asyncSetPersistence(auth, indexedDBLocalPersistence);
            const userCredentials = await asyncSignInWithEmailAndPassword(auth, toLower(email), password);

            if (isObjectNotEmpty(userCredentials) && isObjectNotEmpty(userCredentials.user)) {
                if (isBooleanTrue(userCredentials?.user?.disabled)) {
                    await auth.signOut();
                    localStorage.removeItem('authUser');

                    return false;
                }

                try {
                    const currentUser = { ...userCredentials.user };
                    localStorage.setItem('authUser', JSON.stringify(currentUser));
                } catch (err) {
                    console.log(err);
                }
                //store.dispatch(login(currentUser, true));

                return true;
            }
        } catch (error) {
            throw error;
        }

        return false;
    }

    static doSignInWithCustomToken = async (customToken) => {
        try {
            if (isStringNotEmpty(customToken)) {
                await asyncSetPersistence(auth, indexedDBLocalPersistence);
                const userCredentials = await asyncSignInWithCustomToken(auth, customToken);

                if (isObjectNotEmpty(userCredentials) && isObjectNotEmpty(userCredentials.user)) {
                    if (isBooleanTrue(userCredentials?.user?.disabled)) {
                        await auth.signOut();
                        localStorage.removeItem('authUser');

                        return null;
                    }

                    try {
                        const currentUser = { ...userCredentials.user };
                        localStorage.setItem('authUser', JSON.stringify(currentUser));
                    } catch (err) {
                        console.log(err);
                    }

                    return userCredentials;
                }
            }
        } catch (error) {
            throw error;
        }

        return null;
    }

    // a resume link signs the lead in on whatever device opened the email. when the link belongs to the
    // user already signed in here, the session is left alone rather than replaced
    static doSignInWithResumeLink = async (token) => {
        // only firebase's restored session says who is signed in here: the redux copy can still be loading or
        // can outlive a wiped session, and a sign-in spared on its word leaves the guard open with no id token
        await auth.authStateReady();
        const restoredUserId = auth.currentUser?.uid ?? null;
        const { customToken, userId, leadId } = await AuthUser.redeemResumeLink(token);

        if (isStringNotEmpty(restoredUserId) && restoredUserId === userId) {
            return { userId, leadId, isAlreadySignedIn: true, isUserSwitch: false };
        }

        const userCredentials = await AuthService.doSignInWithCustomToken(customToken);
        if (isObjectEmpty(userCredentials)) {
            throw new Error('Unable to sign in from this link.');
        }

        // only a session firebase itself held counts as another person's; the caller reloads for that one
        return { userId, leadId, isAlreadySignedIn: false, isUserSwitch: isStringNotEmpty(restoredUserId) };
    }

    // claims minted into a custom token live only on the id token, never on the user record
    static getIdTokenClaims = async () => {
        if (auth.currentUser) {
            const result = await auth.currentUser.getIdTokenResult();

            return result?.claims ?? {};
        }

        return {};
    }

    static isResumeLinkSession = async () => {
        const claims = await AuthService.getIdTokenClaims();

        return claims?.signInMethod === 'RESUME_LINK';
    }

    // Sign out
    static doSignOut = async () => {
        try {
            // a browser left subscribed would keep receiving this user's notifications for whoever signs in next
            try {
                await unsubscribeFromPush();
            } catch (error) {
                console.log(error.message);
            }

            await auth.signOut();
            localStorage.removeItem('authUser');

            return true;
        } catch (error) {
            console.log(error.message);
        }

        return false;
    }

    // Password Reset
    static doSendPasswordResetEmail = async (email) => {
        try {
            if (isStringNotEmpty(email)) {
                return await sendPasswordResetLink(toLower(email));
            }
        } catch (error) {
            console.log(error);
        }

        return false;
    }

    // Password Change
    static doPasswordUpdate = async (password) => {
        if (auth.currentUser) {
            await updatePassword(auth.currentUser, password);
        } else {
            throw Error("You must be signed in to change your password.");
        }
    }

    static doPasswordSet = async (user, password) => {
        if (user) {
            try {
                await updatePassword(user, password);

                return true;
            } catch (err) {
                console.log(err);
                return false;
            }
        } else {
            throw Error("You must be signed in to change your password.");
        }
    }

    static doSendEmailVerification = async () => {
        try {
            let displayName = auth?.currentUser?.displayName ?? '';
            let names = displayName.split(' ');
            let firstName = null;
            if (isListNotEmpty(names)) {
                firstName = names[0];
            }

            // await sendEmailVerification(auth.currentUser);
            // return true;
            return await sendEmailVerificationLink(auth?.currentUser?.email, firstName);
        } catch (error) {
            console.log(error);
        }

        return false;
    }

    static doVerifyPasswordResetCode = async (actionCode) => {
        // Verify the password reset code is valid.
        // Returns the email address for the user
        return await verifyPasswordResetCode(auth, actionCode);
    }

    static doResetPassword = async (actionCode, newPassword) => {
        const accountEmail = await AuthService.doVerifyPasswordResetCode(actionCode);

        // Save the new password.
        await confirmPasswordReset(auth, actionCode, newPassword)
        // Password reset has been confirmed and new password updated.
        // console.log('Successfully reset password.');

        try {
            // sign-in the user directly
            return await AuthService.doSignInWithEmailAndPassword(accountEmail, newPassword);
        } catch (error) {
            console.log(error);
            return false;
        }
    }

    static doRecoverEmail = async (actionCode) => {
        // Confirm the action code is valid.
        const info = await checkActionCode(auth, actionCode);

        // Get the restored email address.
        const restoredEmail = info['data']['email'];

        // Revert to the old email.
        await applyActionCode(auth, actionCode);

        // Account email reverted to restoredEmail

        return restoredEmail;
    }

    static doVerifyEmail = async (actionCode) => {
        try {
            // Try to apply the email verification code.
            await applyActionCode(auth, actionCode);
            // Email address has been verified.

            if (isObjectNotEmpty(auth.currentUser)) {
                await auth.currentUser.getIdToken(true);
            }

            return { success: true, currentUser: auth.currentUser };
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    static doCheckForRecent2FARevoke = async (actionCode) => {
        // Confirm the action code is valid.
        const info = await checkActionCode(auth, actionCode)

        // console.log(info);
        // info.data.multiFactorInfo contains the data corresponding to the
        // enrolled second factor that the user is revoking.
        const multiFactorInfo = info['data']['multiFactorInfo'];

        return multiFactorInfo;
    }

    static doRevokeRecent2FA = async (actionCode) => {
        await applyActionCode(auth, actionCode);

        await AuthService.doSignOut();

        return true;
    };

    static isUserSignedIn = () => {
        return isObjectNotEmpty(auth.currentUser);
    }

    // Returns the signed-in user's display name.
    static getDisplayName = () => {
        let firstName = null;
        let lastName = null;
        if (isObjectNotEmpty(auth.currentUser) && isStringNotEmpty(auth.currentUser.displayName)) {
            let names = auth.currentUser.displayName.split(' ');
            if (isListNotEmpty(names)) {
                firstName = names[0];
                if (names.length > 1) {
                    lastName = names[1];
                }
            }
        }

        return { firstName: firstName, lastName: lastName };
    }

    // a generated password is one nobody types - the funnel creates the account without asking for
    // one and the customer sets their own from the welcome email - so it has to be unguessable, not
    // memorable. every character comes from the platform's secure random source with rejection
    // sampling, so none is likelier than another; one of each class satisfies the password policy
    static randomPasswordGenerator = (len) => {
        const length = Number.isInteger(len) && len >= GENERATED_PASSWORD_CHARACTER_SETS.length ? len : 16;
        const allCharacters = GENERATED_PASSWORD_CHARACTER_SETS.join('');
        const characters = GENERATED_PASSWORD_CHARACTER_SETS.map((characterSet) => characterSet[toSecureRandomIndex(characterSet.length)]);
        while (characters.length < length) {
            characters.push(allCharacters[toSecureRandomIndex(allCharacters.length)]);
        }

        // the guaranteed characters were placed first, so their positions would otherwise be known
        for (let index = characters.length - 1; index > 0; index -= 1) {
            const swapIndex = toSecureRandomIndex(index + 1);
            [characters[index], characters[swapIndex]] = [characters[swapIndex], characters[index]];
        }

        return characters.join('');
    }
}

export default AuthService;
